'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useBlockPart, BlockIcon } from '@akademi/site-builder-runtime';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@akademi/ui';
import { X } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { mpSurface, isCartTotalZero, isCartEmpty } from './_shared';
import { tx } from '../_shared/coerce';
import { useMarketplaceCart } from './_useMarketplaceCart';

type Localized = string | Record<string, string>;

export interface MarketplaceCouponCodeProps {
  title?: Localized;
  icon?: string;
  placeholder?: Localized;
  /** Show this block even when the live cart's total is already zero — a
   *  free/fully-discounted cart has nothing left to apply a coupon to.
   *  Ignored with no source mounted. */
  showWhenFree?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/**
 * Discount-code card — akademi-lms-v2's `ShoppingCartPage` coupon widget.
 * With a {@link useMarketplaceCart} source mounted, apply/remove hit the
 * live `/marketplace/cart/coupon` endpoints and the chip list reflects the
 * real cart; with no source it's a local preview list — the "fallbacks
 * stay, for testing" contract.
 */
export function MarketplaceCouponCode({
  title,
  icon = 'ticket',
  placeholder,
  showWhenFree = false,
  paddingOuter,
  paddingInner,
}: MarketplaceCouponCodeProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const cart = useMarketplaceCart();
  const [localApplied, setLocalApplied] = useState<string[]>([]);
  const [code, setCode] = useState('');

  if (cart && isCartEmpty(cart.cart)) return null;
  if (!showWhenFree && cart && isCartTotalZero(cart.cart)) return null;

  const chips: { label: string; onRemove: () => void }[] = cart
    ? (cart.cart?.coupons ?? []).map((c) => ({
        label: c.hashId,
        onRemove: () =>
          void cart.removeCoupon(c.couponId).catch(() => {
            toast.error(t('marketplace.coupon.invalid'), { description: c.hashId });
          }),
      }))
    : localApplied.map((c) => ({
        label: c,
        onRemove: () => setLocalApplied((prev) => prev.filter((x) => x !== c)),
      }));

  async function handleApply() {
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error(t('marketplace.coupon.invalid'));
      return;
    }
    if (chips.some((chip) => chip.label === trimmed)) {
      toast.info(t('marketplace.coupon.duplicate'));
      return;
    }
    if (cart) {
      try {
        await cart.applyCoupon(trimmed);
      } catch {
        toast.error(t('marketplace.coupon.invalid'), { description: trimmed });
        return;
      }
    } else {
      setLocalApplied((prev) => [...prev, trimmed]);
    }
    setCode('');
    toast.success(t('marketplace.coupon.applied'), { description: trimmed });
  }

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardHeader className="p-0">
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
            <BlockIcon value={icon} size={18} />
            {tx(title) || t('marketplace.coupon.title')}
          </CardTitle>
        </CardHeader>
        <CardContent
          className="px-0 pb-0 pt-3"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)' }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }} {...part('form')}>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApply();
              }}
              placeholder={tx(placeholder) || t('marketplace.coupon.placeholder')}
              aria-label={t('marketplace.coupon.title')}
              style={{ flex: 1 }}
            />
            <Button type="button" onClick={handleApply}>
              {t('marketplace.coupon.apply')}
            </Button>
          </div>
          {chips.length > 0 && (
            <div
              style={{ display: 'flex', gap: 'var(--space-2, 8px)', flexWrap: 'wrap' }}
              {...part('list')}
            >
              {chips.map((chip) => (
                <Badge key={chip.label} variant="secondary" style={{ paddingRight: 2 }}>
                  {chip.label}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('marketplace.coupon.remove')}
                    onClick={chip.onRemove}
                    style={{ height: 16, width: 16 }}
                  >
                    <X size={10} />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </SectionShell>
  );
}
